/**
 * Thin fetch wrapper around the Kapitan API.
 *
 * - attaches the in-memory access token when there is one
 * - refreshes once on 401 and replays the request, using the HttpOnly refresh cookie
 * - sends credentials on every call, so that cookie reaches the API
 * - throws `ApiError` on any non-2xx response; callers never inspect raw Response
 */
import { readActiveAgency } from "./activeAgency";
import { singleFlight } from "./single-flight";
import { supportHeaderFor } from "./support";
import { clearAccessToken, readAccessToken, writeAccessToken } from "./tokens";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, payload: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function buildUrl(path: string, query?: Readonly<Record<string, string | number | undefined>>): string {
  const url = new URL(path.replace(/^\//, ""), `${BASE_URL.replace(/\/$/, "")}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") {
    return null;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return text;
  }
  return JSON.parse(text) as unknown;
}

/** The first human-readable string in a DRF error body.
 *
 * DRF is inconsistent by design: `APIException` produces `{"detail": "..."}` but a
 * serializer's `ValidationError` produces `{"field": ["..."]}` -- including
 * `{"detail": ["..."]}` when the key happens to be `detail`. Reading only the string
 * form meant every validation message the backend wrote in Uzbek was thrown away and
 * replaced with "API request failed with status 400".
 *
 * `detail` and `non_field_errors` come first because they are the whole-request
 * messages; otherwise the first field error is better than nothing.
 */
function errorMessage(status: number, payload: unknown): string {
  const firstString = (value: unknown): string | null => {
    if (typeof value === "string") {
      return value;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        const found = firstString(entry);
        if (found !== null) {
          return found;
        }
      }
    }
    return null;
  };

  if (payload !== null && typeof payload === "object") {
    const body = payload as Record<string, unknown>;
    for (const key of ["detail", "non_field_errors", ...Object.keys(body)]) {
      const found = firstString(body[key]);
      if (found !== null) {
        return found;
      }
    }
  }
  return `API request failed with status ${status}`;
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * No body and no stored token: the browser attaches the HttpOnly cookie, the server
 * rotates it and sets the replacement. `credentials: "include"` is what makes that
 * happen at all — the SPA and the API are different origins, so the cookie is not
 * sent by default.
 *
 * Also the boot path: the access token dies with the tab, so on a reload this is how
 * a signed-in person stays signed in.
 *
 * **Single-flight, and it has to be.** The server rotates the refresh token and
 * blacklists the one it replaces, so a second concurrent call presents a cookie the
 * first has already invalidated: 401, access token cleared, person signed out in the
 * middle of their work. That is not hypothetical -- a dashboard firing four queries at
 * once produces four simultaneous 401s the moment the access token expires, and React
 * in development mounts effects twice, which is how this was found.
 */
const refreshAccessToken = singleFlight(async (): Promise<boolean> => {
  const response = await fetch(buildUrl("/api/auth/token/refresh/"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: "{}",
  });
  if (!response.ok) {
    // Only a refusal ends the session. A 429, a 502 or a dropped connection says
    // nothing about whether the person is signed in, and throwing the access token
    // away over one would drop someone out of a screen they were working in over a
    // blip. The caller sees `false` either way and simply does not retry.
    if (response.status === 401 || response.status === 403) {
      clearAccessToken();
    }
    return false;
  }
  const payload = (await response.json()) as { access?: string };
  if (typeof payload.access !== "string") {
    clearAccessToken();
    return false;
  }
  writeAccessToken(payload.access);
  return true;
});

export { refreshAccessToken };

/**
 * Whether the server answered "no such record". A record the caller may not see answers the
 * same 404 as one that does not exist (so ids cannot be probed), and either way retrying is
 * pointless: the screen should say so and lead back to the list, not offer "Qayta urinish".
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** The `code` a refused request carries (`{"detail": ..., "code": ...}`), when there is one. */
export function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.payload === null || typeof error.payload !== "object") {
    return undefined;
  }
  const code = (error.payload as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" | undefined;
  body?: unknown;
  query?: Readonly<Record<string, string | number | undefined>> | undefined;
  /** Skip the Authorization header — used by login and by the refresh call itself. */
  anonymous?: boolean | undefined;
  /** `Idempotency-Key` on a money write, so a retry returns the first answer (D330). */
  idempotencyKey?: string | undefined;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  // A FormData body is sent as it is: the browser sets its multipart boundary itself.
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) {
    headers["content-type"] = "application/json";
  }
  if (options.idempotencyKey !== undefined) {
    headers["idempotency-key"] = options.idempotencyKey;
  }
  if (options.anonymous !== true) {
    const access = readAccessToken();
    if (access !== null) {
      headers.authorization = `Bearer ${access}`;
    }
    // Which agency this browser is working in (TZ §17.2). Sent on every signed-in call, so the
    // server, not this file, decides whether the choice is one the person may make.
    const agency = readActiveAgency();
    if (agency !== null) {
      headers["x-tenant-id"] = String(agency);
    }
    const support = supportHeaderFor(path);
    if (support !== null) {
      headers["x-support-session"] = support;
    }
  }
  return fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers,
    // The refresh token is an HttpOnly cookie on the API origin. Without this the
    // browser would not send it, because the SPA and the API are different origins.
    credentials: "include",
    ...(options.body === undefined
      ? {}
      : { body: options.body instanceof FormData ? options.body : JSON.stringify(options.body) }),
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && options.anonymous !== true && (await refreshAccessToken())) {
    response = await send(path, options);
  }

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, payload, errorMessage(response.status, payload));
  }
  return payload as T;
}

/** The absolute URL of an API path, for links the browser follows itself. */
export function apiUrl(path: string): string {
  return buildUrl(path);
}

/**
 * A binary response, such as a contract PDF. Same bearer token and the same single
 * refresh-and-replay as `apiRequest`; the body is returned as a Blob, not parsed.
 * `anonymous` is for the public signing page, which has no account at all.
 */
export async function apiDownload(
  path: string,
  options: {
    anonymous?: boolean | undefined;
    /** A POST that answers with bytes, such as a Tanlov's PDF. GET when omitted. */
    method?: "GET" | "POST" | undefined;
    body?: unknown;
  } = {},
): Promise<Blob> {
  const request: RequestOptions = {
    anonymous: options.anonymous ?? false,
    method: options.method,
    body: options.body,
  };
  let response = await send(path, request);

  if (response.status === 401 && request.anonymous !== true && (await refreshAccessToken())) {
    response = await send(path, request);
  }

  if (!response.ok) {
    const payload = await parseBody(response);
    throw new ApiError(response.status, payload, errorMessage(response.status, payload));
  }
  return response.blob();
}
